import { useCallback, useEffect, useMemo, useState } from 'react'
import { subscribeMeetings } from '../../services/meetingService'
import {
  subscribeToCalendarPreferences,
  updateCalendarPreferences,
} from '../../services/calendarPreferencesService'
import { useTimeline } from '../../context/useTimeline'
import { useAuth } from '../../context/useAuth'
import { useWorkspace } from '../../context/useWorkspace'
import type {
  CalendarBoardFilterState,
  CalendarPreferences,
  CalendarRangeMode,
  CalendarStyleId,
  TimelineBoardKey,
} from '../../types/schema'
import { CalendarHeader } from './CalendarHeader'
import { CalendarFilterTree } from './CalendarFilterTree'
import { CalendarOptionsSheet } from './CalendarOptionsSheet'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { useChecklistDetailPopup } from '../../context/useChecklistDetailPopup'
import { DayView } from './views/DayView'
import { WeekView } from './views/WeekView'
import { MonthView } from './views/MonthView'
import { AgendaView } from './views/AgendaView'
import { DateStrip } from './DateStrip'
import { useCalendarEvents } from './useCalendarEvents'
import { Fab } from '../Common/Fab'
import { useDragReschedule } from './useDragReschedule'
import { usePersistedState } from '../../hooks/usePersistedState'
import { useIsMobile } from './useIsMobile'
import {
  CALENDAR_KIND_COLORS,
  DEFAULT_CALENDAR_STYLE,
  getCalendarStyle,
  normalizeCalendarStyleId,
} from './calendarThemes'
import {
  addDays,
  isMobileViewportInitial,
  startOfMonthGrid,
  endOfMonthGrid,
  startOfWeek,
  toIsoDate,
  type CalendarEvent,
} from './calendarHelpers'
import { useEduCalendarSessions } from './useEduCalendarSessions'
import { useEduCalendarEvents } from './useEduCalendarEvents'
import { EduSessionDialog } from '../Education/EduSessionDialog'
import { EduEventDetailDialog } from '../Education/EduEventDetailDialog'
import { EduEventEditorDialog } from '../Education/EduEventEditorDialog'
import { EduPtmSlotBoardDialog } from '../Education/EduPtmSlotBoardDialog'
import { canManageEduEvent, type EduEventItem } from '../../services/eduEventService'
import { getEduPtmDay, type EduPtmDayItem } from '../../services/eduPtmService'
import {
  getEduSettings,
  type EduSessionSeries,
} from '../../services/eduTimetableService'
import { subscribeWorkspaceUserDirectory } from '../../services/workspaceUserDirectory'
import { isWorkspaceOwner } from '../../utils/workspaceOwners'
import { resolveWorkspaceTimezone } from '../../utils/eduZonedTime'
import type { EduRoom } from '../../types/schema'
import type { TimetableOccurrence } from '../Education/timetableHelpers'
import './CalendarPage.css'
import type { MeetingRecord } from '../../types/meetings'

interface CalendarPageProps {
  activeBoard: TimelineBoardKey
  onOpenElement: (elementId: string) => void
  onOpenChecklistItem: (parentElementId: string, checklistItemId: string) => void
  onOpenMeeting?: (meetingId: string) => void
  readOnly?: boolean
}

const DEFAULT_FILTER_STATE: CalendarBoardFilterState = { explicit: {}, showAll: true }

const DEFAULT_PREFERENCES: CalendarPreferences = {
  styleId: DEFAULT_CALENDAR_STYLE,
  lastRange: 'week',
  boardScope: { mode: 'current' },
  filterState: {},
  eduSessionsMode: 'MINE',
}

const normalizeRange = (value: unknown): CalendarRangeMode => {
  if (value === 'day' || value === 'week' || value === 'month') return value
  return 'week'
}

export const CalendarPage = ({
  activeBoard,
  onOpenElement,
  onOpenChecklistItem,
  onOpenMeeting,
  readOnly = false,
}: CalendarPageProps) => {
  const { elements, checklistItems, isAccessRestricted } = useTimeline()
  const { user } = useAuth()
  const { activeWorkspaceId, activeRole, workspaces } = useWorkspace()
  const currentWs = workspaces?.find((w) => w.id === activeWorkspaceId)
  const isOwner = activeRole === 'OWNER' || isWorkspaceOwner(currentWs, user?.uid)
  const canViewAllClasses =
    !isAccessRestricted &&
    (activeRole === 'OWNER' || activeRole === 'ADMIN' || activeRole === 'EDITOR' || isOwner)

  const showEduClassesControl = Boolean(
    currentWs?.modules?.teachingEnabled || currentWs?.modules?.trainingEnabled,
  )

  const [directoryMembers, setDirectoryMembers] = useState<
    Array<{ uid: string; displayName: string; role: string }>
  >([])
  const [rooms, setRooms] = useState<EduRoom[]>([])

  useEffect(() => {
    if (!activeWorkspaceId) return
    const unsub = subscribeWorkspaceUserDirectory(activeWorkspaceId, (members) => {
      const eligible = members.filter(
        (m) => m.role === 'OWNER' || m.role === 'ADMIN' || m.role === 'EDITOR',
      )
      setDirectoryMembers(eligible)
    })
    return () => unsub()
  }, [activeWorkspaceId])

  useEffect(() => {
    if (!activeWorkspaceId) return
    let active = true
    getEduSettings(activeWorkspaceId).then((settings) => {
      if (active && settings?.rooms) {
        setRooms(settings.rooms)
      }
    })
    return () => {
      active = false
    }
  }, [activeWorkspaceId])

  const isMobile = useIsMobile()
  const [preferences, setPreferences] = useState<CalendarPreferences>(() => ({
    ...DEFAULT_PREFERENCES,
    lastRange: isMobileViewportInitial() ? 'day' : DEFAULT_PREFERENCES.lastRange,
  }))
  const [focusedDate, setFocusedDate] = useState<Date>(() => new Date())
  const [meetings, setMeetings] = useState<MeetingRecord[]>([])
  const [isFilterOpen, setIsFilterOpen] = usePersistedState<boolean>('calendar.isFilterOpen', () => !isMobileViewportInitial())
  const { openChecklistDraft } = useChecklistDetailPopup()

  const uid = user?.uid ?? null

  useEffect(() => {
    if (!activeWorkspaceId) {
      void Promise.resolve().then(() => setMeetings([]))
      return
    }
    return subscribeMeetings(
      activeWorkspaceId,
      setMeetings,
      (error) => {
        console.warn('[Calendar] meeting subscription failed', error)
        setMeetings([])
      },
      uid ? { restricted: !!isAccessRestricted, uid } : undefined,
    )
  }, [activeWorkspaceId, isAccessRestricted, uid])

  // Hydrate user prefs from Firestore.
  useEffect(() => {
    if (!uid) return
    const unsubscribe = subscribeToCalendarPreferences(uid, (incoming) => {
      setPreferences((current) => ({
        ...current,
        styleId: normalizeCalendarStyleId(incoming.styleId),
        lastRange: normalizeRange(incoming.lastRange ?? current.lastRange),
        boardScope: incoming.boardScope?.mode
          ? { mode: incoming.boardScope.mode, boardKeys: incoming.boardScope.boardKeys ?? [] }
          : current.boardScope,
        filterState: { ...(current.filterState ?? {}), ...(incoming.filterState ?? {}) },
        eduSessionsMode: incoming.eduSessionsMode ?? current.eduSessionsMode ?? 'MINE',
      }))
    })
    return () => unsubscribe()
  }, [uid])

  // Persist prefs whenever they change.
  const persistPreferences = useCallback(
    async (patch: Partial<CalendarPreferences>) => {
      if (!uid) return
      try {
        await updateCalendarPreferences(uid, patch)
      } catch (error) {
        console.warn('[Calendar] persist preferences failed', error)
      }
    },
    [uid],
  )

  const filterState = preferences.filterState?.[activeBoard] ?? DEFAULT_FILTER_STATE
  const effectiveStyleId: CalendarStyleId = normalizeCalendarStyleId(preferences.styleId)

  const setStyleId = (styleId: CalendarStyleId) => {
    setPreferences((current) => ({ ...current, styleId }))
    void persistPreferences({ styleId })
  }
  const setRange = (lastRange: CalendarRangeMode) => {
    setPreferences((current) => ({ ...current, lastRange }))
    void persistPreferences({ lastRange })
  }
  const setBoardScope = (boardScope: CalendarPreferences['boardScope']) => {
    setPreferences((current) => ({ ...current, boardScope }))
    void persistPreferences({ boardScope })
  }
  const setFilterState = (next: CalendarBoardFilterState) => {
    setPreferences((current) => ({
      ...current,
      filterState: { ...(current.filterState ?? {}), [activeBoard]: next },
    }))
    void persistPreferences({
      filterState: { ...(preferences.filterState ?? {}), [activeBoard]: next },
    })
  }

  const setEduSessionsMode = (mode: 'MINE' | 'ALL' | 'OFF') => {
    setPreferences((current) => ({ ...current, eduSessionsMode: mode }))
    void persistPreferences({ eduSessionsMode: mode })
  }

  const range = preferences.lastRange

  useEffect(() => {
    if (preferences.styleId === effectiveStyleId) return
    void Promise.resolve().then(() => {
      setPreferences((current) => ({ ...current, styleId: effectiveStyleId }))
    })
    void persistPreferences({ styleId: effectiveStyleId })
  }, [effectiveStyleId, persistPreferences, preferences.styleId])

  const { windowStart, windowEnd } = useMemo(() => {
    if (range === 'day') {
      const iso = toIsoDate(focusedDate)
      return { windowStart: iso, windowEnd: iso }
    }
    if (range === 'week') {
      const start = startOfWeek(focusedDate)
      const end = addDays(start, 6)
      return { windowStart: toIsoDate(start), windowEnd: toIsoDate(end) }
    }
    const start = startOfMonthGrid(focusedDate)
    const end = endOfMonthGrid(focusedDate)
    return { windowStart: toIsoDate(start), windowEnd: toIsoDate(end) }
  }, [range, focusedDate])

  const eduSessions = useEduCalendarSessions({
    workspaceId: activeWorkspaceId || '',
    uid: user?.uid || '',
    windowStart,
    windowEnd,
    mode: preferences.eduSessionsMode ?? 'MINE',
    directoryMembers,
  })

  const [eduRefreshKey, setEduRefreshKey] = useState<number>(0)
  const triggerEduRefresh = useCallback(() => setEduRefreshKey((k) => k + 1), [])

  const eduEvents = useEduCalendarEvents({
    workspaceId: activeWorkspaceId || '',
    uid: user?.uid || '',
    windowStart,
    windowEnd,
    mode: preferences.eduSessionsMode ?? 'MINE',
    role: activeRole,
    workspace: currentWs,
    elements,
    refreshKey: eduRefreshKey,
  })

  const events = useCalendarEvents({
    elements,
    meetings,
    activeBoard,
    boardScope: preferences.boardScope,
    filterState,
    windowStartIso: windowStart,
    windowEndIso: windowEnd,
    flatChecklistItems: checklistItems,
    eduSessions,
    eduEvents,
  })

  const [selectedSession, setSelectedSession] = useState<{
    occurrence: TimetableOccurrence
    series: EduSessionSeries
  } | null>(null)
  const [selectedEduEvent, setSelectedEduEvent] = useState<EduEventItem | null>(null)
  const [editingEduEvent, setEditingEduEvent] = useState<EduEventItem | null>(null)
  const [selectedPtmSlotBoard, setSelectedPtmSlotBoard] = useState<{
    day: EduPtmDayItem
    teacherUid?: string
  } | null>(null)

  const dragApi = useDragReschedule()

  const applyDragOverride = useCallback(
    (event: CalendarEvent): CalendarEvent => {
      const override = dragApi.overrides[event.id]
      if (!override) return event
      return {
        ...event,
        startDate: override.startDate ?? event.startDate,
        endDate: override.endDate ?? event.endDate,
        startTime: override.startTime ?? event.startTime,
        endTime: override.endTime ?? event.endTime,
      }
    },
    [dragApi.overrides],
  )

  const decoratedAllDay = useMemo(() => events.allDay.map(applyDragOverride), [events.allDay, applyDragOverride])
  const decoratedTimed = useMemo(() => events.timed.map(applyDragOverride), [events.timed, applyDragOverride])
  const decoratedAll = useMemo(() => events.all.map(applyDragOverride), [events.all, applyDragOverride])

  const handleEventClick = (event: CalendarEvent) => {
    if (event.kind === 'edu_ptm' && (event.ptmDay || event.ptmDayId)) {
      if (event.ptmDay) {
        setSelectedPtmSlotBoard({
          day: event.ptmDay as EduPtmDayItem,
          teacherUid: event.ptmTeacherUid,
        })
      } else if (event.ptmDayId) {
        void getEduPtmDay({ workspaceId: activeWorkspaceId || '', dayId: event.ptmDayId }).then((res) => {
          if (res?.day) {
            setSelectedPtmSlotBoard({
              day: res.day,
              teacherUid: event.ptmTeacherUid,
            })
          }
        })
      }
      return
    }
    if (event.kind === 'edu_event' && (event.eduEvent || event.eduEventId)) {
      setSelectedEduEvent((event.eduEvent as EduEventItem) || null)
      return
    }
    if (event.kind === 'session' && (event.eduOccurrence || event.eduSeriesId)) {
      const rawOcc = event.eduOccurrence as Partial<TimetableOccurrence> | undefined
      const occurrence: TimetableOccurrence = {
        id: rawOcc?.id || event.id,
        seriesId: rawOcc?.seriesId || event.eduSeriesId || '',
        date: rawOcc?.date || event.eduSessionDate || event.startDate,
        startTime: rawOcc?.startTime || event.startTime || '09:00',
        durationMin: rawOcc?.durationMin || 60,
        nodeId: rawOcc?.nodeId || event.elementId || '',
        classId: rawOcc?.classId || event.parentElementId || '',
        teacherUids: rawOcc?.teacherUids || [],
        status: rawOcc?.status || 'PLANNED',
        substituteUid: rawOcc?.substituteUid || null,
        topicItemIds: rawOcc?.topicItemIds || [],
        note: rawOcc?.note || null,
        roomId: rawOcc?.roomId,
        onlineUrl: rawOcc?.onlineUrl,
      }
      const rawSeries = event.eduSeries as Partial<EduSessionSeries> | undefined
      const series: EduSessionSeries = {
        id: rawSeries?.id || event.eduSeriesId || occurrence.seriesId,
        workspaceId: rawSeries?.workspaceId || activeWorkspaceId || '',
        rootId: rawSeries?.rootId || '',
        classId: rawSeries?.classId || occurrence.classId || '',
        nodeId: rawSeries?.nodeId || occurrence.nodeId || '',
        module: rawSeries?.module || 'teaching',
        teacherUids: rawSeries?.teacherUids || occurrence.teacherUids || [],
        weekdays: rawSeries?.weekdays || [],
        startTime: rawSeries?.startTime || occurrence.startTime || '09:00',
        durationMin: rawSeries?.durationMin || occurrence.durationMin || 60,
        fromDate: rawSeries?.fromDate || occurrence.date || event.startDate,
        toDate: rawSeries?.toDate || occurrence.date || event.endDate,
        roomId: rawSeries?.roomId ?? occurrence.roomId ?? null,
        onlineUrl: rawSeries?.onlineUrl ?? occurrence.onlineUrl ?? null,
        readAccess: rawSeries?.readAccess || [],
        createdBy: rawSeries?.createdBy || '',
        updatedAt: rawSeries?.updatedAt || new Date(),
      }
      setSelectedSession({ occurrence, series })
      return
    }
    if (event.meetingId) {
      onOpenMeeting?.(event.meetingId)
      return
    }
    if (event.elementId) {
      onOpenElement(event.elementId)
      return
    }
    if (event.checklistItemId && event.parentElementId) {
      onOpenChecklistItem(event.parentElementId, event.checklistItemId)
    }
  }

  const handleCellClick = (date: Date) => {
    if (readOnly || !activeWorkspaceId) return
    const iso = toIsoDate(date)
    openChecklistDraft({
      workspaceId: activeWorkspaceId,
      board: activeBoard,
      scheduleStart: iso,
      scheduleEnd: iso,
      breadcrumb: [{ kind: 'CONTEXT', label: 'Calendar' }],
    })
  }

  const handleRangeSelect = (startIso: string, endIso: string) => {
    if (readOnly || !activeWorkspaceId) return
    openChecklistDraft({
      workspaceId: activeWorkspaceId,
      board: activeBoard,
      scheduleStart: startIso,
      scheduleEnd: endIso,
      breadcrumb: [{ kind: 'CONTEXT', label: 'Calendar' }],
    })
  }

  const handleDayNumberClick = (date: Date) => {
    setFocusedDate(date)
    setRange('day')
  }

  const beginDrag = (event: CalendarEvent, mode: 'move' | 'resize-start' | 'resize-end', iso: string, minutes?: number) => {
    if (readOnly || event.kind === 'session' || event.kind === 'edu_event' || event.kind === 'edu_ptm') return
    dragApi.beginDrag(event, mode, iso, minutes)
  }

  const draggingEventId = dragApi.active?.event.id ?? null
  const draggingOverride = draggingEventId ? dragApi.overrides[draggingEventId] : undefined

  const activeStyle = getCalendarStyle(effectiveStyleId)
  // Only inject the kind (goal/task/…) colors. Surface / grid / text / halo
  // fall through to the app theme via CSS-var chains in CalendarPage.css, so
  // the calendar background auto-matches whichever of the 7 themes is active.
  const cssVars = useMemo<Record<string, string>>(() => ({
    '--cal-goal': CALENDAR_KIND_COLORS.goal,
    '--cal-task': CALENDAR_KIND_COLORS.task,
    '--cal-subtask': CALENDAR_KIND_COLORS.subtask,
    '--cal-checklist': CALENDAR_KIND_COLORS.checklist,
    '--cal-meeting': CALENDAR_KIND_COLORS.meeting,
    '--cal-session': CALENDAR_KIND_COLORS.session,
  }), [])

  // Portalled menus (FloatingMenu) render into document.body and escape the
  // calendar root's CSS-var scope. They still need the kind colors for dots.
  const menuVars = cssVars

  // On mobile, the range tab still drives the underlying window for filtering,
  // but the visible surface is always: date strip + agenda list. The grid views
  // are unusable at <=640px so we swap them for the Retasks-style structure.
  const showMobileAgenda = isMobile

  return (
    <section
      className={`calRoot${activeStyle.glass ? ' isGlass' : ''}${isMobile ? ' isMobile' : ''}`}
      data-calendar-style={activeStyle.id}
      style={cssVars as React.CSSProperties}
    >
      <CalendarHeader
        styleId={activeStyle.id}
        onStyleChange={setStyleId}
        range={range}
        onRangeChange={setRange}
        focusedDate={focusedDate}
        onFocusedDateChange={setFocusedDate}
        activeBoard={activeBoard}
        boardScope={preferences.boardScope}
        onBoardScopeChange={setBoardScope}
        isFilterOpen={isFilterOpen}
        onToggleFilter={() => setIsFilterOpen((current) => !current)}
        isMobile={isMobile}
        onOpenOptions={() => setIsFilterOpen(true)}
        menuVars={menuVars}
      />

      {showMobileAgenda && (
        <DateStrip
          focusedDate={focusedDate}
          events={decoratedAll}
          range={range}
          onDateClick={(date) => setFocusedDate(date)}
        />
      )}

      <div className="calBody">
        {isFilterOpen && !isMobile && (
          <CalendarFilterTree
            elements={elements.filter((element) => element.board === activeBoard ||
              preferences.boardScope.boardKeys?.includes(element.board))}
            filterState={filterState}
            onChange={setFilterState}
            eduSessionsMode={preferences.eduSessionsMode ?? 'MINE'}
            onEduSessionsModeChange={setEduSessionsMode}
            canViewAllClasses={canViewAllClasses}
            showEduClassesControl={showEduClassesControl}
          />
        )}

        <div className="calCanvas">
          {showMobileAgenda ? (
            <AgendaView
              focusedDate={focusedDate}
              allDayEvents={decoratedAllDay}
              timedEvents={decoratedTimed}
              onEventClick={handleEventClick}
              onCellClick={handleCellClick}
            />
          ) : (
            <>
              {range === 'month' && (
                <MonthView
                  focusedDate={focusedDate}
                  events={decoratedAll}
                  onEventClick={handleEventClick}
                  onDayNumberClick={handleDayNumberClick}
                  onCellClick={handleCellClick}
                  onRangeSelect={handleRangeSelect}
                  onEventDragStart={(event, dateIso) => beginDrag(event, 'move', dateIso)}
                  onEventDragOver={(dateIso) => dragApi.updateDrag(dateIso)}
                  onEventDragEnd={() => void dragApi.commitDrag()}
                  overrideStartIso={draggingOverride?.startDate}
                  overrideEndIso={draggingOverride?.endDate}
                  draggingEventId={draggingEventId}
                />
              )}
              {range === 'week' && (
                <WeekView
                  focusedDate={focusedDate}
                  allDayEvents={decoratedAllDay}
                  timedEvents={decoratedTimed}
                  onEventClick={handleEventClick}
                  onCellClick={handleCellClick}
                  onEventDragStart={(event, dateIso, minutes) => beginDrag(event, 'move', dateIso, minutes)}
                  onEventDragOver={(dateIso, minutes) => dragApi.updateDrag(dateIso, minutes)}
                  onEventDragEnd={() => void dragApi.commitDrag()}
                  draggingEventId={draggingEventId}
                />
              )}
              {range === 'day' && (
                <DayView
                  focusedDate={focusedDate}
                  allDayEvents={decoratedAllDay}
                  timedEvents={decoratedTimed}
                  onEventClick={handleEventClick}
                  onCellClick={handleCellClick}
                  onEventDragStart={(event, dateIso, minutes) => beginDrag(event, 'move', dateIso, minutes)}
                  onEventDragOver={(dateIso, minutes) => dragApi.updateDrag(dateIso, minutes)}
                  onEventDragEnd={() => void dragApi.commitDrag()}
                  draggingEventId={draggingEventId}
                />
              )}
            </>
          )}
        </div>
      </div>

      {isMobile && (
        <Fab
          className="calFab"
          onClick={() => handleCellClick(focusedDate)}
          label="Add checklist"
          icon={<AddRoundedIcon fontSize="medium" />}
          disabled={readOnly || !activeWorkspaceId}
        />
      )}

      {isMobile && (
        <CalendarOptionsSheet
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          styleId={activeStyle.id}
          onStyleChange={setStyleId}
          activeBoard={activeBoard}
          boardScope={preferences.boardScope}
          onBoardScopeChange={setBoardScope}
          elements={elements.filter((element) => element.board === activeBoard ||
            preferences.boardScope.boardKeys?.includes(element.board))}
          filterState={filterState}
          onFilterChange={setFilterState}
          eduSessionsMode={preferences.eduSessionsMode ?? 'MINE'}
          onEduSessionsModeChange={setEduSessionsMode}
          canViewAllClasses={canViewAllClasses}
          showEduClassesControl={showEduClassesControl}
        />
      )}

      {selectedSession && (
        <EduSessionDialog
          isOpen={true}
          onClose={() => setSelectedSession(null)}
          occurrence={selectedSession.occurrence}
          series={selectedSession.series}
          workspaceId={activeWorkspaceId || ''}
          timezone={currentWs?.timezone}
          activeRole={activeRole}
          currentUserId={user?.uid}
          isOwner={isOwner}
          elements={elements}
          rooms={rooms}
          directoryMembers={directoryMembers}
          onSessionUpdated={() => {
            // Updated
          }}
        />
      )}

      {selectedEduEvent && (
        <EduEventDetailDialog
          isOpen={Boolean(selectedEduEvent)}
          onClose={() => setSelectedEduEvent(null)}
          workspaceId={activeWorkspaceId || ''}
          event={selectedEduEvent}
          isAdmin={isOwner || activeRole === 'ADMIN'}
          canEdit={canManageEduEvent(selectedEduEvent, {
            isAdmin: isOwner || activeRole === 'ADMIN',
            isOwner,
            uid: user?.uid || '',
            elements,
          })}
          onEdit={(evt) => {
            setSelectedEduEvent(null)
            setEditingEduEvent(evt)
          }}
          onRefresh={triggerEduRefresh}
        />
      )}

      {editingEduEvent && (
        <EduEventEditorDialog
          isOpen={Boolean(editingEduEvent)}
          onClose={() => setEditingEduEvent(null)}
          workspaceId={activeWorkspaceId || ''}
          activeRole={activeRole || ''}
          isOwner={isOwner}
          currentUserId={user?.uid || ''}
          elements={elements}
          event={editingEduEvent}
          timezone={currentWs?.timezone}
          onSaved={() => {
            setEditingEduEvent(null)
            triggerEduRefresh()
          }}
        />
      )}

      {selectedPtmSlotBoard && (
        <EduPtmSlotBoardDialog
          isOpen={Boolean(selectedPtmSlotBoard)}
          onClose={() => setSelectedPtmSlotBoard(null)}
          workspaceId={activeWorkspaceId || ''}
          day={selectedPtmSlotBoard.day}
          isAdmin={isOwner || activeRole === 'ADMIN'}
          currentUserId={user?.uid || ''}
          elements={elements}
          initialTeacherUid={selectedPtmSlotBoard.teacherUid}
          timezone={resolveWorkspaceTimezone(currentWs)}
          directoryMembers={directoryMembers}
        />
      )}
    </section>
  )
}
